import { Icon } from "@iconify/react";
import { motion, useInView } from "framer-motion";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";

const Color = {
  themeBlue: "#4ea4f5",
  agentAccent: "#4ea4f5",
  borderLight: "#c4c4c7",
  lineDivider: "#e5e5e5",
  surfacePanel: "#fcfcfb",
  textPrimary: "#0d0d0d",
  textSecondary: "#5a5a5d",
  textHintStrong: "#979797",
};

const handledItems = [
  "Connected sources turned into a morning brief.",
  "Prepared a client-call agenda with fit, implementation, pricing, and next steps.",
];

const todayItems = [
  "11:00-11:30 AM: client decision call.",
  "Use the call to decide whether this is worth deeper diligence.",
];

const proposalItems = [
  {
    title: "Run client call prep",
    detail: "I prepared the sharper decision brief from your agenda and notes.",
  },
  {
    title: "Turn pitch draft into review-ready version",
    detail:
      "I gathered the source notes and outline so it does not become last-minute work.",
  },
  {
    title: "Set up automation: pre-meeting briefs",
    detail:
      "You keep repeating this prep flow, so I can prepare it automatically.",
  },
];

const connectorItems = [
  { label: "Mail", icon: "lucide:mail" },
  { label: "Drive", icon: "lucide:folder-open" },
  { label: "Calendar", icon: "lucide:calendar-days" },
  { label: "Chat", icon: "lucide:messages-square" },
];

const agentBenefitItems = [
  {
    icon: "lucide:calendar-check",
    textKey: "modern.agentEnterprise.benefitDay",
  },
  {
    icon: "lucide:users-round",
    textKey: "modern.agentEnterprise.benefitRelationships",
  },
  {
    icon: "lucide:list-checks",
    textKey: "modern.agentEnterprise.benefitTasks",
  },
];

const graphNodes = [
  { label: "You", x: 50, y: 52, size: 42, color: "#0f9f8c", active: true },
  { label: "Avery", x: 27, y: 75, size: 24, color: "#19a8d8" },
  { label: "Pitch", x: 43, y: 28, size: 26, color: "#2f6cf6" },
  { label: "Jordan", x: 26, y: 39, size: 28, color: "#2f6cf6" },
  { label: "Client", x: 54, y: 31, size: 24, color: "#1fa5d3" },
  { label: "API", x: 38, y: 49, size: 30, color: "#2f6cf6" },
  { label: "Deck", x: 64, y: 66, size: 28, color: "#2f6cf6" },
  { label: "Design", x: 76, y: 45, size: 22, color: "#f59e0b" },
  { label: "Repo", x: 72, y: 27, size: 21, color: "#f59e0b" },
  { label: "Investor", x: 35, y: 70, size: 22, color: "#19a8d8" },
  { label: "Calendar", x: 61, y: 40, size: 24, color: "#9aa4b2" },
  { label: "Mail", x: 81, y: 66, size: 18, color: "#9aa4b2" },
  { label: "Chat", x: 17, y: 57, size: 18, color: "#9aa4b2" },
  { label: "Notes", x: 47, y: 82, size: 20, color: "#19a8d8" },
];

interface FeatureSectionAgentEnterpriseProps {
  showDescription?: boolean;
  autoStart?: boolean;
}

const FeatureSectionAgentEnterprise: React.FC<
  FeatureSectionAgentEnterpriseProps
> = ({ showDescription = true, autoStart = false }) => {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const inViewResult = useInView(cardRef, { amount: 0.3 });
  const isInView = autoStart || inViewResult;

  return (
    <section
      id="agent-enterprise"
      className={`${showDescription ? (autoStart ? "py-6 md:py-12" : "py-8 md:py-20") : "py-0"} bg-white overflow-hidden`}
    >
      <div className={showDescription ? "container mx-auto px-4" : ""}>
        <div
          className={`${showDescription ? `grid md:grid-cols-2 gap-10 lg:gap-14 max-w-7xl mx-auto ${autoStart ? "items-start" : "items-stretch"}` : "flex justify-center"}`}
          style={showDescription ? { minHeight: "auto" } : undefined}
        >
          {/* Text Content - Left */}
          {showDescription && (
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              {...(autoStart
                ? { animate: { opacity: 1, x: 0 } }
                : {
                    whileInView: { opacity: 1, x: 0 },
                    viewport: { once: true },
                  })}
              transition={{ duration: 0.6 }}
              className="order-1 flex flex-col"
            >
              {/* Top group */}
              <div>
                <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                  style={{ backgroundColor: `${Color.themeBlue}15` }}
                >
                  <Icon icon="lucide:bot" style={{ color: Color.themeBlue }} />
                  <span
                    style={{ color: Color.themeBlue }}
                    className="font-medium text-sm"
                  >
                    {t("modern.agentEnterprise.badge")}
                  </span>
                </div>

                <h2 className="text-3xl md:text-5xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight mt-0">
                  {t("modern.agentEnterprise.title")}
                </h2>

                <p
                  className="text-lg md:text-xl leading-relaxed"
                  style={{ color: "#726F6F" }}
                >
                  {t("modern.agentEnterprise.subtitle")}
                </p>
              </div>

              {/* Spacer */}
              <div className="flex-1 min-h-[16px] max-h-[32px]" />

              {/* Bottom group: bullets */}
              <div className="space-y-4">
                {agentBenefitItems.map((item) => (
                  <div className="flex items-center gap-3" key={item.textKey}>
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${Color.themeBlue}15` }}
                    >
                      <Icon
                        icon={item.icon}
                        className="text-lg"
                        style={{ color: Color.themeBlue }}
                      />
                    </div>
                    <span className="text-base text-gray-700">
                      {t(item.textKey)}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Visual Demo - Right */}
          <motion.div
            initial={{ opacity: 0, x: showDescription ? 50 : 0 }}
            {...(autoStart
              ? { animate: { opacity: 1, x: 0 } }
              : {
                  whileInView: { opacity: 1, x: 0 },
                  viewport: { once: true },
                })}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-2 px-4 md:px-0 flex flex-col "
            style={{ paddingTop: "68px" }}
          >
            <div className="relative mx-auto w-full max-w-[620px]">
              <div
                ref={cardRef}
                className="relative min-h-[470px] overflow-hidden rounded-[24px] bg-white shadow-[0_18px_46px_rgba(17,24,39,0.08)] sm:min-h-[430px]"
                style={{
                  border: `1px solid ${Color.borderLight}`,
                }}
              >
                <div className="absolute inset-0 grid min-h-0 grid-cols-[52fr_48fr] text-[#0d0d0d]">
                  <div
                    className="min-w-0 overflow-hidden border-r bg-white p-3"
                    style={{ borderColor: Color.lineDivider }}
                  >
                    <div
                      className="mb-2 flex rounded-[13px] border bg-white p-1"
                      style={{ borderColor: Color.lineDivider }}
                    >
                      <div className="flex flex-1 items-center justify-center gap-1.5 rounded-[10px] bg-[#f3f3f5] px-2 py-1.5">
                        <Icon
                          icon="lucide:chart-no-axes-combined"
                          className="text-[12px]"
                          style={{ color: Color.themeBlue }}
                        />
                        <span className="truncate text-[11px] font-semibold leading-3 text-[#0d0d0d]">
                          Assistant Brief
                        </span>
                      </div>
                      <div className="flex flex-1 items-center justify-center gap-1.5 px-2 py-1.5 text-[#7d7d80]">
                        <Icon icon="lucide:sparkles" className="text-[12px]" />
                        <span className="truncate text-[11px] font-medium leading-3">
                          Knowledge
                        </span>
                      </div>
                    </div>

                    <div className="mb-2">
                      <p className="m-0 text-[20px] font-bold leading-6 text-[#0d0d0d]">
                        Friday · July 3
                      </p>
                      <p className="m-0 mt-1 text-[11px] leading-4 text-[#5a5a5d]">
                        Your AI prepared today from connected sources, notes,
                        and relationships.
                      </p>
                    </div>

                    <div
                      className="mb-2 rounded-[14px] border bg-[#fcfcfb] p-2"
                      style={{ borderColor: Color.lineDivider }}
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-[11px] font-semibold leading-3 text-[#0d0d0d]">
                          Connect sources
                        </span>
                        <span className="text-[10px] leading-3 text-[#7d7d80]">
                          4 available
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-1.5">
                        {connectorItems.map((item) => (
                          <div
                            className="flex h-8 flex-col items-center justify-center gap-0.5 rounded-[10px] bg-white px-1 text-[#5a5a5d]"
                            key={item.label}
                          >
                            <Icon
                              icon={item.icon}
                              className="text-[12px]"
                              style={{ color: Color.agentAccent }}
                            />
                            <span className="truncate text-[8px] font-medium leading-3">
                              {item.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div
                      className="mb-2 rounded-[14px] border bg-white p-2.5"
                      style={{ borderColor: Color.lineDivider }}
                    >
                      <p className="m-0 text-[12.5px] font-semibold leading-4 text-[#0d0d0d]">
                        Today is a good leverage day, not a catch-up day.
                      </p>
                      <p className="m-0 mt-1 text-[10px] leading-[13px] text-[#5a5a5d]">
                        Use the client call to decide quickly, then spend open
                        time tightening the pitch narrative.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div
                        className="rounded-[14px] border bg-white p-2"
                        style={{ borderColor: Color.lineDivider }}
                      >
                        <div className="mb-1.5 flex items-center gap-1.5">
                          <Icon
                            icon="lucide:check"
                            className="text-[12px] text-[#10B981]"
                          />
                          <span className="text-[11px] font-semibold text-[#0d0d0d]">
                            Handled
                          </span>
                        </div>
                        <ul className="m-0 space-y-1 p-0">
                          {handledItems.map((item) => (
                            <li
                              className="flex gap-1.5 text-[9.5px] leading-[13px] text-[#5a5a5d]"
                              key={item}
                            >
                              <span className="mt-[5px] h-1 w-1 shrink-0 rounded-full bg-[#10B981]" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div
                        className="rounded-[14px] border bg-white p-2"
                        style={{ borderColor: Color.lineDivider }}
                      >
                        <div className="mb-1.5 flex items-center gap-1.5">
                          <Icon
                            icon="lucide:calendar-days"
                            className="text-[12px]"
                            style={{ color: Color.themeBlue }}
                          />
                          <span className="text-[11px] font-semibold text-[#0d0d0d]">
                            Today
                          </span>
                        </div>
                        <ul className="m-0 space-y-1 p-0">
                          {todayItems.map((item) => (
                            <li
                              className="flex gap-1.5 text-[9.5px] leading-[13px] text-[#5a5a5d]"
                              key={item}
                            >
                              <span
                                className="mt-[5px] h-1 w-1 shrink-0 rounded-full"
                                style={{ backgroundColor: Color.themeBlue }}
                              />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div
                      className="mt-2 rounded-[14px] border bg-white p-2.5"
                      style={{ borderColor: Color.lineDivider }}
                    >
                      <div className="mb-2 flex items-center gap-1.5">
                        <Icon
                          icon="lucide:wand-sparkles"
                          className="text-[12px]"
                          style={{ color: Color.themeBlue }}
                        />
                        <span className="text-[11px] font-semibold text-[#0d0d0d]">
                          Shall I...
                        </span>
                      </div>
                      <div className="grid gap-2">
                        {proposalItems.slice(0, 2).map((item, index) => (
                          <motion.div
                            animate={
                              isInView
                                ? { opacity: 1, y: 0 }
                                : { opacity: 0, y: 8 }
                            }
                            className="border-t pt-2 first:border-t-0 first:pt-0"
                            initial={{ opacity: 0, y: 8 }}
                            key={item.title}
                            style={{ borderColor: Color.lineDivider }}
                            transition={{
                              delay: 0.08 + index * 0.08,
                              duration: 0.24,
                            }}
                          >
                            <div className="text-[10.5px] font-semibold leading-4 text-[#0d0d0d]">
                              {item.title}
                            </div>
                            <p className="m-0 text-[9.5px] leading-[13px] text-[#7d7d80]">
                              {item.detail}
                            </p>
                            {index < 2 ? (
                              <div className="mt-1.5 flex gap-1.5">
                                <span className="rounded-[8px] bg-[#eaf5ff] px-2 py-1 text-[9px] font-semibold text-[#226fb7]">
                                  Yes, do it
                                </span>
                                <span
                                  className="rounded-[8px] border px-2 py-1 text-[9px] font-semibold text-[#5a5a5d]"
                                  style={{ borderColor: Color.lineDivider }}
                                >
                                  Skip
                                </span>
                              </div>
                            ) : null}
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="min-w-0 bg-[#fcfcfb] p-3">
                    <div
                      className="mb-2 flex rounded-[13px] border bg-white p-1"
                      style={{ borderColor: Color.lineDivider }}
                    >
                      {["Profile", "Relationships", "Map"].map((tab) => (
                        <div
                          className={`flex-1 rounded-[10px] px-2 py-1.5 text-center text-[10px] font-medium leading-3 ${
                            tab === "Map"
                              ? "bg-[#f3f3f5] text-[#0d0d0d]"
                              : "text-[#7d7d80]"
                          }`}
                          key={tab}
                        >
                          {tab}
                        </div>
                      ))}
                    </div>

                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="m-0 text-[10.5px] leading-4 text-[#5a5a5d]">
                        Knowledge graph from people, tasks, connected sources,
                        and files.
                      </p>
                      <Icon
                        icon="lucide:share-2"
                        className="shrink-0 text-[13px] text-[#7d7d80]"
                      />
                    </div>

                    <div
                      className="relative mx-auto aspect-square w-full max-w-[205px] overflow-hidden rounded-[18px] border bg-white"
                      style={{ borderColor: Color.lineDivider }}
                    >
                      {graphNodes
                        .filter((node) => !node.active)
                        .map((node) => {
                          const dx = node.x - 50;
                          const dy = node.y - 52;
                          const length = Math.sqrt(dx * dx + dy * dy);
                          const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
                          return (
                            <span
                              aria-hidden="true"
                              className="absolute left-1/2 top-[52%] h-px origin-left bg-[#e5e5e5]"
                              key={`line-${node.label}`}
                              style={{
                                width: `${length}%`,
                                transform: `rotate(${angle}deg)`,
                              }}
                            />
                          );
                        })}

                      {graphNodes.map((node, index) => (
                        <motion.div
                          animate={
                            isInView
                              ? { opacity: 1, scale: 1 }
                              : { opacity: 0, scale: 0.9 }
                          }
                          className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
                          initial={{ opacity: 0, scale: 0.9 }}
                          key={node.label}
                          style={{
                            left: `${node.x}%`,
                            top: `${node.y}%`,
                            zIndex: node.active ? 3 : 2,
                          }}
                          transition={{
                            delay: 0.1 + index * 0.025,
                            duration: 0.22,
                          }}
                        >
                          <span
                            className="rounded-full border-2 border-white shadow-[0_1px_4px_rgba(17,24,39,0.16)]"
                            style={{
                              width: node.size,
                              height: node.size,
                              backgroundColor: node.color,
                            }}
                          />
                          <span
                            className={`${node.active ? "mt-1 text-[10px] text-[#0d0d0d]" : "mt-0.5 text-[8px] text-[#5a5a5d]"} max-w-[58px] truncate leading-3`}
                          >
                            {node.label}
                          </span>
                        </motion.div>
                      ))}
                    </div>

                    <div
                      className="mt-2 rounded-[14px] border bg-white p-2.5"
                      style={{ borderColor: Color.lineDivider }}
                    >
                      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#7d7d80]">
                        Ready result
                      </div>
                      <p className="m-0 text-[11px] font-semibold leading-4 text-[#0d0d0d]">
                        A daily brief, next actions, and an automation proposal
                        are ready.
                      </p>
                      <div
                        className="mt-2 flex h-8 items-center gap-2 rounded-[16px] border bg-white pl-3 pr-1.5 shadow-[0_3px_8px_rgba(0,0,0,0.03)]"
                        style={{ borderColor: Color.borderLight }}
                      >
                        <span className="min-w-0 flex-1 truncate text-[10px] leading-3 text-[#979797]">
                          Ask Avery to prepare my brief
                        </span>
                        <span
                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border bg-white opacity-70"
                          style={{ borderColor: Color.borderLight }}
                        >
                          <Icon
                            icon="ion:arrow-up"
                            className="text-[13px]"
                            style={{ color: Color.agentAccent }}
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FeatureSectionAgentEnterprise;
